/**
 * Everything the app knows about running inside an iframe (Canvas, Brightspace,
 * Simple Syllabus, ...) lives here. Third parties such as Stripe Checkout
 * refuse to render inside a frame, so any flow that must leave the frame goes
 * through `navigateOutsideIframe`.
 */

export function areWeInIframe() {
  return (
    window !== window.parent ||
    window !== window.top ||
    window.self !== window.top
  )
}

/**
 * Opens `url` in a new tab. Returns false when the browser's popup blocker
 * swallowed the request, which happens when the call is not close enough to
 * a user gesture.
 */
export function openInNewTab(url: string): boolean {
  // Not passing the `noopener` feature on purpose: with it, window.open
  // always returns null, and we need the handle to tell "opened" from
  // "blocked". Nulling `opener` below gives the same effect. If that
  // assignment throws (some browsers do for a cross-origin handle), the new
  // tab keeps `window.opener` until its first cross-origin navigation
  // lands. That window is accepted: the only destination is Stripe
  // Checkout, and the tab starts on about:blank with nothing to reach back
  // into.
  const opened = window.open(url, '_blank')
  if (!opened) return false
  try {
    opened.opener = null
  } catch {
    // See above.
  }
  return true
}

/**
 * Sends the user to `url` in a way that escapes the iframe when there is one.
 *
 * - Not in an iframe: navigate the current window, as usual.
 * - In an iframe: open a new tab so the destination loads at top level.
 * - Popup blocked: nothing happened. The caller must show the user a link
 *   they can click themselves.
 */
export function navigateOutsideIframe(
  url: string
): 'navigated' | 'opened_new_tab' | 'blocked' {
  if (!areWeInIframe()) {
    window.location.href = url
    return 'navigated'
  }
  return openInNewTab(url) ? 'opened_new_tab' : 'blocked'
}
